import React, { useState, useEffect, useRef } from "react";
import styles from "./Withdraw.module.css";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import EditWithdrawModal from "../EditWithdrawModal/EditWithdrawModal";
import DeleteWithdrawModal from "./Modal/DeleteWithdrawModal";
import AxiosUrl from "../AxiosUrl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import useMediaQuery from "@mui/material/useMediaQuery";
import Avatar from "@mui/material/Avatar";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import EditIcon from "@mui/icons-material/Edit";
import format from 'date-fns/format';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import subMonths from 'date-fns/subMonths';

// Get current time in Bangladesh timezone so date filters match server (Asia/Dhaka)
const getNowBD = () => new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Dhaka" }));

function Withdraw() {
  const uid = localStorage.getItem("id");
  const [withdrawals, setWithdrawals] = useState([]);
  const [open, setOpenModal] = useState(false);
  const [edit, setEdit] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [withdrawalToDelete, setWithdrawalToDelete] = useState(null);
  
  // Date filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  
  // Media queries for responsive design
  const isMobile = useMediaQuery('(max-width:880px)');
  const isSmallMobile = useMediaQuery('(max-width:480px)');
  
  // View mode: 'grid' or 'list' - default to list
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    if (!open && !deleteModalOpen) {
      getWithdrawals();
    }
  }, [refresh, open, deleteModalOpen, page, statusFilter, searchTerm, dateFrom, dateTo, dateFilter]);

  const getWithdrawals = () => {
    setLoading(true);
    
    // Defensive check for uid
    if (!uid) {
      console.error("User ID is missing");
      setLoading(false);
      return;
    }
    
    // Prepare date params
    let dateParams = {};
    
    try {
      if (dateFilter === "today") {
        const today = getNowBD();
        dateParams = {
          dateFrom: format(startOfDay(today), 'yyyy-MM-dd\'T\'HH:mm:ss'),
          dateTo: format(endOfDay(today), 'yyyy-MM-dd\'T\'HH:mm:ss')
        };
      } else if (dateFilter === "yesterday") {
        const yesterday = subDays(getNowBD(), 1);
        dateParams = {
          dateFrom: format(startOfDay(yesterday), 'yyyy-MM-dd\'T\'HH:mm:ss'),
          dateTo: format(endOfDay(yesterday), 'yyyy-MM-dd\'T\'HH:mm:ss')
        };
      } else if (dateFilter === "thisWeek") {
        const now = getNowBD();
        const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday as week start
        dateParams = {
          dateFrom: format(startOfDay(weekStart), 'yyyy-MM-dd\'T\'HH:mm:ss'),
          dateTo: format(endOfDay(now), 'yyyy-MM-dd\'T\'HH:mm:ss')
        };
      } else if (dateFilter === "lastWeek") {
        const now = getNowBD();
        const lastWeekStart = startOfWeek(subDays(now, 7), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subDays(now, 7), { weekStartsOn: 1 });
        dateParams = {
          dateFrom: format(startOfDay(lastWeekStart), 'yyyy-MM-dd\'T\'HH:mm:ss'),
          dateTo: format(endOfDay(lastWeekEnd), 'yyyy-MM-dd\'T\'HH:mm:ss')
        };
      } else if (dateFilter === "thisMonth") {
        const now = getNowBD();
        const monthStart = startOfMonth(now);
        dateParams = {
          dateFrom: format(startOfDay(monthStart), 'yyyy-MM-dd\'T\'HH:mm:ss'),
          dateTo: format(endOfDay(now), 'yyyy-MM-dd\'T\'HH:mm:ss')
        };
      } else if (dateFilter === "lastMonth") {
        const now = getNowBD();
        const lastMonthStart = startOfMonth(subMonths(now, 1));
        const lastMonthEnd = endOfMonth(subMonths(now, 1));
        dateParams = {
          dateFrom: format(startOfDay(lastMonthStart), 'yyyy-MM-dd\'T\'HH:mm:ss'),
          dateTo: format(endOfDay(lastMonthEnd), 'yyyy-MM-dd\'T\'HH:mm:ss')
        };
      } else if (dateFrom && dateTo) {
        try {
          const fromDate = new Date(dateFrom);
          const toDate = new Date(dateTo);
          dateParams = {
            dateFrom: format(startOfDay(fromDate), 'yyyy-MM-dd\'T\'HH:mm:ss'),
            dateTo: format(endOfDay(toDate), 'yyyy-MM-dd\'T\'HH:mm:ss')
          };
        } catch (err) {
          console.error("Date format error:", err);
        }
      }
    } catch (error) {
      console.error("Error preparing date parameters:", error);
      setLoading(false);
      return;
    }
    
    try {
      AxiosUrl.post(`/fetch-withdraw/${uid}`, {
        page,
        limit,
        status: statusFilter,
        search: searchTerm,
        ...dateParams
      })
        .then((res) => {
          if (res.data && res.data.withdrawals) {
            setWithdrawals(res.data.withdrawals);
            setTotalPages(res.data.totalPages || 1);
          } else {
            console.error("Invalid response format:", res.data);
            setWithdrawals([]);
            setTotalPages(1);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("API error:", error);
          setWithdrawals([]);
          setTotalPages(1);
          setLoading(false);
          toast.error("Failed to fetch withdrawals. Please try again.");
        });
    } catch (error) {
      console.error("Unexpected error:", error);
      setWithdrawals([]);
      setTotalPages(1);
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleStatusChange = (event) => {
    try {
      if (event && event.target && event.target.value !== undefined) {
        setStatusFilter(event.target.value);
        setPage(1);
      } else {
        console.error("Invalid event or event.target.value in handleStatusChange");
      }
    } catch (error) {
      console.error("Error in handleStatusChange:", error);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleDeleteClick = (withdrawal) => {
    setWithdrawalToDelete(withdrawal);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setWithdrawalToDelete(null);
  };

  const handleDeleteSuccess = () => {
    setRefresh((prev) => prev + 1);
  };

  const handleDateFilterChange = (event) => {
    try {
      if (!event || !event.target || event.target.value === undefined) {
        console.error("Invalid event in handleDateFilterChange");
        return;
      }
      
      const value = event.target.value;
      setDateFilter(value);
      
      const today = getNowBD();

      if (value === "today") {
        setDateFrom(format(today, 'yyyy-MM-dd'));
        setDateTo(format(today, 'yyyy-MM-dd'));
      } else if (value === "yesterday") {
        const yesterday = subDays(today, 1);
        setDateFrom(format(yesterday, 'yyyy-MM-dd'));
        setDateTo(format(yesterday, 'yyyy-MM-dd'));
      } else if (value === "thisWeek") {
        const weekStart = startOfWeek(today, { weekStartsOn: 1 });
        setDateFrom(format(weekStart, 'yyyy-MM-dd'));
        setDateTo(format(today, 'yyyy-MM-dd'));
      } else if (value === "lastWeek") {
        const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        setDateFrom(format(lastWeekStart, 'yyyy-MM-dd'));
        setDateTo(format(lastWeekEnd, 'yyyy-MM-dd'));
      } else if (value === "thisMonth") {
        const monthStart = startOfMonth(today);
        setDateFrom(format(monthStart, 'yyyy-MM-dd'));
        setDateTo(format(today, 'yyyy-MM-dd'));
      } else if (value === "lastMonth") {
        const lastMonthStart = startOfMonth(subMonths(today, 1));
        const lastMonthEnd = endOfMonth(subMonths(today, 1));
        setDateFrom(format(lastMonthStart, 'yyyy-MM-dd'));
        setDateTo(format(lastMonthEnd, 'yyyy-MM-dd'));
      } else if (value === "all") {
        setDateFrom("");
        setDateTo("");
      }
      
      setPage(1);
    } catch (error) {
      console.error("Error in handleDateFilterChange:", error);
      // Set safe default values
      setDateFilter("all");
      setDateFrom("");
      setDateTo("");
    }
  };
  
  const handleDateFromChange = (event) => {
    try {
      if (event && event.target && event.target.value !== undefined) {
        setDateFrom(event.target.value);
        setPage(1);
      } else {
        console.error("Invalid event in handleDateFromChange");
      }
    } catch (error) {
      console.error("Error in handleDateFromChange:", error);
    }
  };
  
  const handleDateToChange = (event) => {
    try {
      if (event && event.target && event.target.value !== undefined) {
        setDateTo(event.target.value);
        setPage(1);
      } else {
        console.error("Invalid event in handleDateToChange");
      }
    } catch (error) {
      console.error("Error in handleDateToChange:", error);
    }
  };

  return (
    <div className={styles.withdrawMain}>
      <ToastContainer />
      
      {open && (
        <EditWithdrawModal
          setOpenModal={setOpenModal}
          data={edit}
          setRefresh={() => setRefresh((prev) => prev + 1)}
        />
      )}
      
      <DeleteWithdrawModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        withdrawalData={withdrawalToDelete}
        onSuccess={handleDeleteSuccess}
      />
      
      <div className={styles.withdrawContentWrapper}>
        <div className={styles.withdrawFilterSection}>
          <div className={styles.withdrawFilterItem}>
            <div className={styles.withdrawFilterLabel}>Search</div>
            <TextField
              className={styles.withdrawSearchField}
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={styles.withdrawSearchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          
          <div className={styles.withdrawFilterItem}>
            <div className={styles.withdrawFilterLabel}>Status</div>
            <TextField
              select
              className={styles.withdrawStatusFilter}
              value={statusFilter || "all"}
              onChange={handleStatusChange}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </div>
          
          <div className={styles.withdrawFilterItem}>
            <div className={styles.withdrawFilterLabel}>Date Filter</div>
            <TextField
              select
              className={styles.withdrawDateFilter}
              value={dateFilter || "all"}
              onChange={handleDateFilterChange}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="thisWeek">This Week</MenuItem>
              <MenuItem value="lastWeek">Last Week</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
              <MenuItem value="lastMonth">Last Month</MenuItem>
            </TextField>
          </div>
          
          <div className={styles.withdrawFilterItem}>
            <div className={styles.withdrawFilterLabel}>From Date</div>
            <TextField
              type="date"
              className={styles.withdrawFromDate}
              value={dateFrom || ""}
              onChange={handleDateFromChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>
          <div className={styles.withdrawFilterItem}>
            <div className={styles.withdrawFilterLabel}>To Date</div>
            <TextField
              type="date"
              className={styles.withdrawToDate}
              value={dateTo || ""}
              onChange={handleDateToChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>
        </div>

        {/* View Toggle Buttons */}
        <div className={styles.withdrawViewToggle}>
          <IconButton
            onClick={() => setViewMode('grid')}
            color={viewMode === 'grid' ? 'primary' : 'default'}
            title="Grid View"
            style={{
              backgroundColor: viewMode === 'grid' ? '#1976d2' : '#f5f5f5',
              color: viewMode === 'grid' ? 'white' : '#666',
              marginRight: '8px'
            }}
          >
            <ViewModuleIcon />
          </IconButton>
          <IconButton
            onClick={() => setViewMode('list')}
            color={viewMode === 'list' ? 'primary' : 'default'}
            title="List View"
            style={{
              backgroundColor: viewMode === 'list' ? '#1976d2' : '#f5f5f5',
              color: viewMode === 'list' ? 'white' : '#666'
            }}
          >
            <ViewListIcon />
          </IconButton>
        </div>

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className={styles.withdrawGridContainer}>
            {loading && <div className={styles.googleProgressBar}></div>}
            {withdrawals.length <= 0 && !loading ? (
              <div className={styles.withdrawNoDataAvailable}>
                No data available
              </div>
            ) : (
              <div className={styles.withdrawGrid}>
                {withdrawals.map((withdrawal) => (
                  <Card key={withdrawal._id} className={styles.withdrawGridCard}>
                    <CardContent className={styles.withdrawGridCardContent}>
                      <div className={styles.withdrawGridHeader}>
                        <div className={styles.withdrawGridAvatarContainer}>
                          {withdrawal.user?.photo ? (
                            <Avatar 
                              alt={withdrawal.user?.username || "User"} 
                              src={withdrawal.user?.photo} 
                              className={styles.withdrawGridAvatar}
                            />
                          ) : (
                            <Avatar className={styles.withdrawGridAvatar}>
                              <AccountCircleIcon />
                            </Avatar>
                          )}
                        </div>
                        <div className={styles.withdrawGridHeaderInfo}>
                          <Typography variant="h6" className={styles.withdrawGridUsername}>
                            {withdrawal.user?.username || "Unknown"}
                          </Typography>
                          <Chip
                            label={withdrawal.Status === 0 ? "PENDING" : withdrawal.Status === 1 ? "APPROVED" : "REJECTED"}
                            size="small"
                            className={styles.withdrawGridStatusChip}
                            style={{
                              backgroundColor: 
                                withdrawal.Status === 0 ? "#e9a13a" : 
                                withdrawal.Status === 1 ? "#4caf50" : "#f44336",
                              color: "white",
                              fontSize: "11px",
                              height: "24px",
                              fontWeight: "600",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className={styles.withdrawGridDetails}>
                        <div className={styles.withdrawGridDetailItem}>
                          <Typography variant="caption" className={styles.withdrawGridLabel}>Type:</Typography>
                          <Typography variant="body2" className={styles.withdrawGridValue}>{withdrawal.Type}</Typography>
                        </div>
                        
                        <div className={styles.withdrawGridDetailItem}>
                          <Typography variant="caption" className={styles.withdrawGridLabel}>Number:</Typography>
                          <Typography variant="body2" className={styles.withdrawGridValue}>{withdrawal.Number}</Typography>
                        </div>
                        
                        <div className={styles.withdrawGridDetailItem}>
                          <Typography variant="caption" className={styles.withdrawGridLabel}>Amount:</Typography>
                          <Chip
                            label={`৳${withdrawal.Amount}`}
                            size="small"
                            style={{
                              backgroundColor: "#9575cd",
                              color: "white",
                              fontSize: "12px",
                              height: "26px",
                              fontWeight: "600",
                              boxShadow: "0 2px 4px rgba(149, 117, 205, 0.3)"
                            }}
                          />
                        </div>
                      </div>
                    </CardContent>
                    <CardActions className={styles.withdrawGridActions}>
                      <Button
                        className={styles.withdrawEditButton}
                        onClick={() => {
                          setEdit(withdrawal);
                          setOpenModal(true);
                        }}
                        size="small"
                        startIcon={<EditIcon />}
                      >
                        Edit
                      </Button>
                      <Button
                        className={styles.withdrawDeleteButton}
                        onClick={() => handleDeleteClick(withdrawal)}
                        size="small"
                        startIcon={<DeleteIcon />}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* List/Table View */}
        {viewMode === 'list' && (
        <TableContainer 
          component={Paper}
          className={styles.withdrawTableContainer}
        >
          {loading && <div className={styles.googleProgressBar}></div>}
          <Table className={styles.withdrawTable} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="center" className={styles.withdrawImageCell}>Image</TableCell>
                <TableCell align="center">User</TableCell>
                <TableCell align="left">Type</TableCell>
                <TableCell align="center">Number</TableCell>
                <TableCell align="center">Amount</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawals.length <= 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <div className={styles.withdrawNoDataAvailable}>No data available</div>
                  </TableCell>
                </TableRow>
              ) : (
                withdrawals.map((withdrawal) => (
                  <TableRow 
                    key={withdrawal._id} 
                    className={styles.withdrawTableRow}
                  >
                    <TableCell align="center" className={styles.withdrawImageCell}>
                      {withdrawal.user?.photo ? (
                        <Avatar 
                          alt={withdrawal.user?.username || "User"} 
                          src={withdrawal.user?.photo} 
                          className={styles.withdrawUserAvatar}
                        />
                      ) : (
                        <Avatar 
                          alt={withdrawal.user?.username || "User"} 
                          className={`${styles.withdrawUserAvatar} ${styles.withdrawUserAvatarEmpty}`}
                        >
                          <AccountCircleIcon className={styles.withdrawAvatarPlaceholder} />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {withdrawal.user?.username || "Unknown"}
                    </TableCell>
                    <TableCell align="left">{withdrawal.Type}</TableCell>
                    <TableCell align="center">{withdrawal.Number}</TableCell>
                    <TableCell align="center">{withdrawal.Amount}</TableCell>
                    <TableCell align="center">
                      <Button
                        className={`${styles.withdrawStatusButton} ${
                          withdrawal.Status === 0
                            ? styles.withdrawPendingButton
                            : withdrawal.Status === 1
                            ? styles.withdrawApprovedButton
                            : styles.withdrawRejectedButton
                        }`}
                      >
                        {withdrawal.Status === 0
                          ? "PENDING"
                          : withdrawal.Status === 1
                          ? "APPROVED"
                          : "REJECTED"}
                      </Button>
                    </TableCell>
                    <TableCell align="center" className={styles.withdrawActionsCell}>
                      <div className={styles.withdrawActionsWrapper}>
                        <Button
                          className={styles.withdrawEditButton}
                          onClick={() => {
                            setEdit(withdrawal);
                            setOpenModal(true);
                          }}
                          disableRipple
                          disableFocusRipple
                          disableElevation
                        >
                          <span className={styles.withdrawButtonText}>Edit</span>
                        </Button>
                        <Button
                          className={styles.withdrawDeleteButton}
                          onClick={() => handleDeleteClick(withdrawal)}
                          disableRipple
                          disableFocusRipple
                          disableElevation
                        >
                          <DeleteIcon className={styles.withdrawDeleteIcon} />
                          <span className={styles.withdrawButtonText}>Delete</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        
        {/* Pagination - shown for both views */}
        <div className={styles.withdrawPaginationContainer}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton={!isSmallMobile}
            showLastButton={!isSmallMobile}
            size={isMobile ? "small" : "medium"}
            siblingCount={isMobile ? 0 : 1}
            boundaryCount={isMobile ? 0 : 1}
            shape="rounded"
          />
        </div>
      </div>
    </div>
  );
}

export default Withdraw;
